import {
  Dispatch,
  MouseEvent,
  MouseEventHandler,
  PointerEvent,
  PointerEventHandler,
  SetStateAction,
  useCallback,
} from "react";
import {nominate} from "platformed-client/types";
import {Vec2} from "./math/vec2";
import {SpatialIndex} from "./spatialIndex";
import {
  CELL_SIZE,
  ClientDiagram,
  ClientDiagramConnection,
  ClientDiagramNode,
  DiagramAction,
  DiagramAnyObjectId,
  getAnyObject,
} from "./types";
import {Camera} from "./camera";
import {unreachableCase} from "../../../../../../../utils/typescript";
import {generateConnectionPath, getConnectionWaypoints, removeRedundantAnchors} from "./connectionPath";
import {getDefaultIcon} from "../icons";

export const TOOLS = ["pan", "select", "createNode", "createConnection"] as const;
export type Tool = (typeof TOOLS)[number];

export type DrawingConnection = {start: Vec2; end: Vec2};
export type SelectToolDragState = {object: DiagramAnyObjectId; lastPosition: Vec2; isDuplicate: boolean};

export type ToolState =
  | {tool: "pan"}
  | {tool: "select"; selectedObjectId: DiagramAnyObjectId | null; dragging: SelectToolDragState | null}
  | {tool: "createNode"}
  | {tool: "createConnection"; drawing: DrawingConnection | null};

export function defaultToolState(tool: Tool): ToolState {
  switch (tool) {
    case "pan":
      return {tool: "pan"};
    case "select":
      return {tool: "select", selectedObjectId: null, dragging: null};
    case "createNode":
      return {tool: "createNode"};
    case "createConnection":
      return {tool: "createConnection", drawing: null};
    default:
      unreachableCase(tool);
  }
}

/** Clears up references to objects that do not exist in the data flow. */
export function fixupToolState(prev: ToolState, diagram: ClientDiagram): ToolState {
  const next = {...prev};

  switch (next.tool) {
    case "pan":
      break;
    case "select":
      {
        if (next.selectedObjectId != null && getAnyObject(next.selectedObjectId, diagram) == null) {
          next.selectedObjectId = null;
        }

        if (next.dragging != null && getAnyObject(next.dragging.object, diagram) == null) {
          next.dragging = null;
        }
      }
      break;
    case "createConnection":
      break;
    case "createNode":
      break;
    default:
      unreachableCase(next);
  }

  return next;
}

type ToolEventHandlers<T extends Tool> = {
  onPointerDown?: (ev: PointerEvent, ctx: ToolEventHandlerContext<T>) => void;
  onPointerMove?: (ev: PointerEvent, ctx: ToolEventHandlerContext<T>) => void;
  onPointerUp?: (ev: PointerEvent, ctx: ToolEventHandlerContext<T>) => void;
  onClick?: (ev: MouseEvent, ctx: ToolEventHandlerContext<T>) => void;
  onDoubleClick?: (ev: MouseEvent, ctx: ToolEventHandlerContext<T>) => void;
};

export type UseToolEventHandlersContext = {
  toolState: ToolState;
  setToolState: Dispatch<SetStateAction<ToolState>>;
  containerElement: HTMLDivElement;
  diagram: ClientDiagram;
  dispatchDiagram: Dispatch<DiagramAction>;
  spatialIndex: SpatialIndex;
  camera: Camera;
  setCamera: Dispatch<SetStateAction<Camera>>;
};

export type UseToolEventHandlers = {
  onPointerDown: PointerEventHandler;
  onPointerMove: PointerEventHandler;
  onPointerUp: PointerEventHandler;
  onClick: MouseEventHandler;
  onDoubleClick: MouseEventHandler;
};

type ToolEventHandlerContext<T extends Tool> = Omit<UseToolEventHandlersContext, "toolState"> & {
  toolState: Extract<ToolState, {tool: T}>;
};

const TOOL_EVENT_HANDLERS: {[T in Tool]: ToolEventHandlers<T>} = {
  pan: {}, // Handled in `usePanControls` for now.
  select: {
    onPointerDown: (ev, ctx) => {
      const cellCoords = getCellCoords(ev, ctx);
      const object = ctx.spatialIndex.getFirst(cellCoords);

      if (object != null) {
        const isDuplicate = ev.altKey && object.type === "Node";
        let dragObject = object;

        // If Alt/Option is held and we're clicking on a node, create a duplicate
        if (isDuplicate) {
          const originalNode = ctx.diagram.nodes.get(object.id);
          if (originalNode != null) {
            const duplicateNode: ClientDiagramNode = {
              ...originalNode,
              id: nominate("DiagramNodeId", window.crypto.randomUUID()),
            };
            ctx.dispatchDiagram({action: "createNode", node: duplicateNode});
            dragObject = {type: "Node", id: duplicateNode.id};
          }
        }

        ctx.setToolState(prev => {
          if (prev.tool !== "select") return prev;
          return {...prev, dragging: {object: dragObject, lastPosition: cellCoords, isDuplicate}};
        });
      }
    },
    onDoubleClick: (ev, ctx) => {
      const cellCoords = getCellCoords(ev, ctx);
      createNodeAtCell(cellCoords, ctx);
    },
    onPointerMove: (ev, ctx) => {
      const cellCoords = getCellCoords(ev, ctx);
      if (ctx.toolState.dragging == null) return;
      if (cellCoords.equals(ctx.toolState.dragging.lastPosition)) return;

      if (ctx.toolState.dragging.object.type === "Node") {
        const node = ctx.diagram.nodes.get(ctx.toolState.dragging.object.id);

        if (node == null) {
          console.error("Dragging node failed: could not find node.");
          return;
        }

        const updatedNode: ClientDiagramNode = {...node, position: cellCoords};
        ctx.dispatchDiagram({action: "updateNode", node: updatedNode});
      }

      if (ctx.toolState.dragging.object.type === "Connection") {
        const connection = ctx.diagram.connections.get(ctx.toolState.dragging.object.id);

        if (connection == null) {
          console.error("Dragging connection failed: could not find connection.");
          return;
        }

        // If we're dragging a connection, and not an anchor, it means we must
        // create a new anchor point.

        /** The point on the current path (before movement) where we should create the anchor. */
        const pointOnPath = ctx.toolState.dragging.lastPosition;

        // Determine the new anchor index by traversing the connection path
        // and counting how many anchors we hit up to the anchor point.
        let anchorIndex = 0;
        const path = generateConnectionPath(getConnectionWaypoints(connection, ctx.diagram.nodes));
        for (const point of path) {
          if (point.equals(pointOnPath)) break;
          if (connection.anchors.find(anchor => anchor.position.equals(point))) anchorIndex++;
        }

        ctx.dispatchDiagram({
          action: "updateConnection",
          connection: {
            ...connection,
            anchors: [
              ...connection.anchors.slice(0, anchorIndex),
              {position: cellCoords},
              ...connection.anchors.slice(anchorIndex),
            ],
          },
        });

        // Update the drag state to indicate we are now dragging the newly created anchor.
        ctx.setToolState(prev => {
          if (prev.tool !== "select" || prev.dragging == null || prev.dragging.object.type !== "Connection") {
            return prev;
          }

          return {
            ...prev,
            dragging: {
              ...prev.dragging,
              object: {type: "ConnectionAnchor", id: connection.id, index: anchorIndex},
              isDuplicate: false,
            },
          };
        });
      }

      if (ctx.toolState.dragging.object.type === "ConnectionAnchor") {
        const connection = ctx.diagram.connections.get(ctx.toolState.dragging.object.id);

        if (connection == null) {
          console.error("Moving connection anchor failed: could not find connection.");
          return;
        }

        const anchorIndex = ctx.toolState.dragging.object.index;

        ctx.dispatchDiagram({
          action: "updateConnection",
          connection: {
            ...connection,
            anchors: connection.anchors.map((anchor, index) =>
              index === anchorIndex ? {...anchor, position: cellCoords} : anchor,
            ),
          },
        });
      }

      // Immediately select the dragged object.
      ctx.setToolState(prev => {
        if (prev.tool !== "select" || prev.dragging == null) return prev;
        return {...prev, selectedObjectId: prev.dragging.object};
      });

      // Update the last position.
      ctx.setToolState(prev => {
        if (prev.tool !== "select" || prev.dragging == null) return prev;
        return {...prev, dragging: {...prev.dragging, lastPosition: cellCoords}};
      });
    },
    onPointerUp: (ev, ctx) => {
      const cellCoords = getCellCoords(ev, ctx);

      if (ctx.toolState.dragging != null) {
        // Remove anchors that are dropped on adjacent anchors / nodes on the
        // same connection.
        if (ctx.toolState.dragging.object.type === "ConnectionAnchor") {
          const connection = ctx.diagram.connections.get(ctx.toolState.dragging.object.id);

          if (connection == null) {
            console.error("Dropping connection anchor failed: could not find connection.");
            return;
          }

          const newAnchors = removeRedundantAnchors(connection, ctx.diagram.nodes);

          if (newAnchors.length !== connection.anchors.length) {
            ctx.dispatchDiagram({action: "updateConnection", connection: {...connection, anchors: newAnchors}});
          }
        }
      }

      // Select what is under the cursor.
      ctx.setToolState(prev => {
        if (prev.tool !== "select") return prev;
        return {...prev, selectedObjectId: ctx.spatialIndex.getFirst(cellCoords)};
      });

      // We're no longer dragging
      ctx.setToolState(prev => {
        if (prev.tool !== "select" || prev.dragging == null) return prev;
        return {...prev, dragging: null};
      });
    },
  },
  createNode: {
    onClick: (ev, ctx) => {
      const cellCoords = getCellCoords(ev, ctx);
      createNodeAtCell(cellCoords, ctx);
    },
  },
  createConnection: {
    onPointerDown: (ev, ctx) => {
      const cellCoords = getCellCoords(ev, ctx);
      const nodeId = ctx.spatialIndex.getNode(cellCoords);
      if (nodeId == null) return;

      ctx.setToolState(prev => {
        if (prev.tool !== "createConnection") return prev;
        return {...prev, drawing: {start: cellCoords, end: cellCoords}};
      });
    },
    onPointerMove: (ev, ctx) => {
      const cellCoords = getCellCoords(ev, ctx);

      ctx.setToolState(prev => {
        if (prev.tool !== "createConnection") return prev;
        if (prev.drawing == null || prev.drawing.end.equals(cellCoords)) return prev;
        return {...prev, drawing: {...prev.drawing, end: cellCoords}};
      });
    },
    onPointerUp: (_ev, ctx) => {
      if (ctx.toolState.drawing == null) return;
      const startNode = ctx.spatialIndex.getNode(ctx.toolState.drawing.start);
      const endNode = ctx.spatialIndex.getNode(ctx.toolState.drawing.end);

      if (
        startNode == null ||
        endNode == null ||
        // Can't connect a node to itself
        startNode === endNode ||
        // Can't draw over an existing connection
        ctx.diagram.connections.values().find(c => c.from === startNode && c.to === endNode) !== undefined
      ) {
        ctx.setToolState(prev => {
          if (prev.tool !== "createConnection") return prev;
          return {...prev, drawing: null};
        });

        return;
      }

      const connection: ClientDiagramConnection = {
        id: nominate("DiagramConnectionId", window.crypto.randomUUID()),
        from: startNode,
        to: endNode,
        label: "",
        color: "blue",
        anchors: [],
      };

      ctx.dispatchDiagram({action: "createConnection", connection});
      ctx.setToolState({tool: "select", selectedObjectId: {type: "Connection", id: connection.id}, dragging: null});
    },
  },
};

function getCellCoords(
  ev: PointerEvent | MouseEvent,
  ctx: Pick<UseToolEventHandlersContext, "containerElement" | "camera">,
): Vec2 {
  const viewportRect = ctx.containerElement.getBoundingClientRect();
  const screenPos = new Vec2(ev.clientX - viewportRect.left, ev.clientY - viewportRect.top);
  const worldPos = ctx.camera.s2w(screenPos);
  return worldPos.divScalar(CELL_SIZE).floor();
}

function createNodeAtCell(
  cellCoords: Vec2,
  ctx: Pick<UseToolEventHandlersContext, "spatialIndex" | "dispatchDiagram" | "setCamera" | "setToolState">,
): void {
  // Can't create a node on top of another node.
  if (ctx.spatialIndex.getNode(cellCoords) != null) {
    return;
  }

  const node: ClientDiagramNode = {
    id: nominate("DiagramNodeId", window.crypto.randomUUID()),
    icon: getDefaultIcon().id,
    label: "Node",
    description: "",
    position: cellCoords,
  };

  ctx.dispatchDiagram({action: "createNode", node});

  ctx.setCamera(prev => {
    const next = prev.clone();
    next.targetPosition = cellCoords.mulScalar(CELL_SIZE).addScalar(0.5 * CELL_SIZE);
    return next;
  });

  ctx.setToolState({tool: "select", selectedObjectId: {type: "Node", id: node.id}, dragging: null});
}

export function useToolEventHandlers(ctx: UseToolEventHandlersContext): UseToolEventHandlers {
  const onPointerDown = useCallback(
    (ev: PointerEvent) => {
      TOOL_EVENT_HANDLERS[ctx.toolState.tool].onPointerDown?.(ev, ctx as any);
    },
    [ctx],
  );

  const onPointerMove = useCallback(
    (ev: PointerEvent) => {
      TOOL_EVENT_HANDLERS[ctx.toolState.tool].onPointerMove?.(ev, ctx as any);
    },
    [ctx],
  );

  const onPointerUp = useCallback(
    (ev: PointerEvent) => {
      TOOL_EVENT_HANDLERS[ctx.toolState.tool].onPointerUp?.(ev, ctx as any);
    },
    [ctx],
  );

  const onClick = useCallback(
    (ev: MouseEvent) => {
      TOOL_EVENT_HANDLERS[ctx.toolState.tool].onClick?.(ev, ctx as any);
    },
    [ctx],
  );

  const onDoubleClick = useCallback(
    (ev: MouseEvent) => {
      TOOL_EVENT_HANDLERS[ctx.toolState.tool].onDoubleClick?.(ev, ctx as any);
    },
    [ctx],
  );

  return {onPointerDown, onPointerMove, onPointerUp, onClick, onDoubleClick};
}
